import { useEffect, useState } from "react";
import { createServerFn } from "@tanstack/react-start";
import * as db from "@/lib/db.server";
import { SPECIALTIES } from "@/data/heallexa";
import { specialtySlug } from "@/lib/seo-slugs";

export type CanonicalSpecialty = { slug: string; label: string; count: number };

// Public read: no user-scoped data, so no auth middleware.
//
// Returns the ~69 CANONICAL specialties, not the ~486 raw NUCC taxonomy values
// that live in providers.specialty ("Retina Specialist (Ophthalmology)
// Physician"). Before the taxonomy existed this merged every distinct raw
// value into the picker, which is why the dropdown read as inconsistent.
// See aws-migration/sql/2026-07-31-specialty-taxonomy.sql.
export const listCanonicalSpecialtiesFn = createServerFn({ method: "GET" }).handler(async () => {
  const rows = await db.query<{ slug: string; label: string; provider_count: number }>(
    `SELECT slug, label, provider_count
       FROM public.specialty_canonical
      ORDER BY provider_count DESC, label ASC`,
  );
  return rows.map((r) => ({ slug: r.slug, label: r.label, count: Number(r.provider_count) }));
});

// Typeahead. One indexed lookup over ~1k terms — canonical labels, subspecialty
// terms auto-extracted from the raw taxonomy ("retina"), and hand-written
// patient language incl. Spanish ("braces", "root canal", "pediatra").
// Trigram-backed, so typos resolve ("opthamologist" -> Ophthalmologists).
// ~5ms server-side; no AI call in the keystroke path.
//
// MUST be POST. CloudFront in front of the SSR Lambda caches
// /_serverFn/<hash> WITHOUT the ?payload= query string in the cache key, so a
// GET server fn whose result depends on its arguments serves the first
// caller's response to everyone. Verified in prod 2026-07-31: after deploying
// this as a GET, "braces", "pediatra" and "root canal" all returned the
// response cached for "ret" (x-cache: Hit from cloudfront). POST is not
// cached. Any argument-dependent server fn reachable from the browser has the
// same constraint.
export const suggestSpecialtiesFn = createServerFn({ method: "POST" })
  .inputValidator((input: { q?: string; limit?: number }) => ({
    q: typeof input?.q === "string" ? input.q.trim().slice(0, 80) : "",
    limit: Math.min(Math.max(Number(input?.limit) || 8, 1), 25),
  }))
  .handler(async ({ data }) => {
    if (!data.q) return [];
    try {
      const rows = await db.query<{
        slug: string; label: string; matched_term: string; provider_count: number;
      }>(
        `SELECT * FROM public.suggest_specialties($1, $2)`,
        [data.q, data.limit],
      );
      return rows.map((r) => ({
        slug: r.slug,
        label: r.label,
        // Shown as "Ophthalmologists · retina" when the hit came from a
        // synonym rather than the label itself.
        matchedTerm: r.matched_term === r.label.toLowerCase() ? null : r.matched_term,
        count: Number(r.provider_count),
      }));
    } catch {
      return [];
    }
  });

let cache: CanonicalSpecialty[] | null = null;
let inflight: Promise<CanonicalSpecialty[]> | null = null;

const FALLBACK: CanonicalSpecialty[] = SPECIALTIES.map((label) => ({
  slug: specialtySlug(label),
  label,
  count: 0,
}));

async function fetchSpecialties(): Promise<CanonicalSpecialty[]> {
  if (cache) return cache;
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const data = await listCanonicalSpecialtiesFn();
      cache = data?.length ? data : FALLBACK;
      return cache;
    } catch {
      cache = FALLBACK;
      return cache;
    } finally {
      inflight = null;
    }
  })();
  return inflight;
}

// Must stay inside a server fn: this module is imported by client-reachable
// route files (providers.$id, providers.$state.$city_.$specialty), so touching
// db.server directly from an exported plain function pulls the server-only
// import into the client bundle and the Vite build rejects it.
// POST for the same CloudFront cache-key reason as suggestSpecialtiesFn: this
// runs in route loaders, which execute in the browser on client-side
// navigation, so a GET would let one specialty's label be served for another's
// slug.
const resolveSpecialtySlugFn = createServerFn({ method: "POST" })
  .inputValidator((slug: unknown) => String(slug ?? "").toLowerCase().slice(0, 120))
  .handler(async ({ data: target }) => {
    if (!target) return null;
    try {
      const row = await db.one<{ label: string }>(
        `SELECT label FROM public.specialty_canonical WHERE slug = $1`,
        [target],
      );
      if (row?.label) return row.label;
    } catch { /* fall through to the legacy path */ }
    try {
      const rows = await db.query<{ raw_lower: string }>(
        `SELECT raw_lower FROM public.specialty_taxonomy`,
      );
      const match = rows.find((r) => specialtySlug(r.raw_lower) === target);
      return match ? match.raw_lower : null;
    } catch {
      return null;
    }
  });

/**
 * Resolves a specialty slug for /providers/* routes. Canonical slugs are the
 * primary key of specialty_canonical, so this is a PK lookup. Falls back to
 * matching a raw taxonomy value's slug so URLs indexed before the taxonomy
 * landed keep resolving instead of 404ing.
 */
export async function resolveDbSpecialtySlug(slug: string): Promise<string | null> {
  try {
    return await resolveSpecialtySlugFn({ data: slug });
  } catch {
    return null;
  }
}

/** Every canonical specialty, cached process-wide so it loads once per session. */
export function useCanonicalSpecialties(): CanonicalSpecialty[] {
  const [list, setList] = useState<CanonicalSpecialty[]>(cache ?? FALLBACK);
  useEffect(() => {
    let cancelled = false;
    fetchSpecialties().then((l) => { if (!cancelled) setList(l); });
    return () => { cancelled = true; };
  }, []);
  return list;
}
